/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useMemo, useRef, useState } from 'react'
import { cva } from 'class-variance-authority'
import { cn } from '~/lib/utils'
import { useIntersectionObserver } from 'usehooks-ts'

import { Progress } from '~/components/UI/shadcn/progress'

import MarkDown from '~/components/Helpers/MarkDown'
import BlockVideo from '~/components/Media/components/BlockVideo'
import Image from '~/components/Helpers/Image'
import TertiaryLink from '~/components/UI/Buttons/TertiaryLink/TertiaryLink'
import ControlButton from '~/components/UI/Buttons/ControlButton/ControlButton'
import { useQuery } from '@tanstack/react-query'
import { getEntriesByIds } from '~/lib/contensis/misc'
import Loader from '~/components/UI/General/Loader/Loader'

const carouselVariants = cva('', {
  variants: {
    overlayOpacity: {
      '80': 'to-black/80 via-black/80',
      '60': 'to-black/60 via-black/60',
      '40': 'to-black/40 via-black/40',
      '20': 'to-black/20 via-black/20',
      '0': ''
    }
  },
  defaultVariants: {
    overlayOpacity: '60'
  }
})

const FullSlide = ({ slide }: FullSlideProps) => {
  const {
    heading,
    copy,
    image,
    video,
    overlayOpacity,
    buttons,
    expandedCopy,
    accentColour
  } = slide
  const button = buttons ? buttons[0] : null

  const [isVisible, setIsVisible] = useState(false)
  // const [, setHasLoaded] = useState(false)

  // const { isIntersecting, ref } = useIntersectionObserver({
  //   threshold: 0.5
  // })

  // const setLoaded = useCallback(() => {
  //   if (inView) setHasLoaded(true)
  // }, [inView, setHasLoaded])

  function toggleOverlay() {
    setIsVisible(!isVisible)
  }

  return (
    <div className='sticky top-[70px] z-0 sm:h-[150vh] lg:top-[90px]'>
      <div
        // ref={ref}
        className='gap-s sticky top-[70px] flex h-[calc(100dvh-70px)] flex-col items-center !pt-0 sm:top-[90px] sm:h-[calc(100dvh-90px)]'
      >
        <div className='flex h-[calc(100dvh-70px)] w-full items-center justify-center sm:h-[calc(100dvh-90px)]'>
          {video && image ? (
            <BlockVideo
              videoUrl={video?.sys.uri}
              // inView={inView}
              width={''}
              height={''}
              image={image}
              // onLoad={setLoaded}
              // pause={!isIntersecting}
              btnPosition={'top-right'}
              btnClassName='lg:!right-l lg:!top-l max-lg:w-[36px] max-lg:h-[36px]'
            />
          ) : (
            image && <Image image={image} />
          )}
        </div>
        <div
          className={cn(
            carouselVariants({ overlayOpacity: overlayOpacity || '60' }),
            'absolute h-full w-full bg-gradient-to-b from-transparent'
          )}
        ></div>
        <div className='pt-m px-l pb-3xl sm:py-xl lg:pt-3xl lg:mx-l lg:pb-3xl absolute container h-full w-full'>
          <div className='gap-s max-sm:mr-xs flex h-full flex-col justify-end text-white sm:max-w-[600px] lg:max-w-[660px]'>
            <MarkDown content={`### ${heading}`} />
            <MarkDown
              className={cn(
                'transition-all',
                `${expandedCopy && !isVisible ? '-mb-s delay-300' : 'mb-0'}`
              )}
              content={copy}
            />
            {expandedCopy && (
              <MarkDown
                className={cn(
                  'flex flex-col overflow-hidden transition-all duration-500',
                  `${isVisible ? 'max-h-full' : 'max-h-0'}`
                )}
                content={expandedCopy}
              />
            )}
            {button && !expandedCopy && (
              <div className='mt-s w-fit'>
                <TertiaryLink data={button} colour={'white'} />
              </div>
            )}
            {expandedCopy && (
              <div
                className={cn(
                  'sm:mt-xs z-1 flex w-fit justify-end [&_img]:transition-transform [&_img]:duration-500',
                  `${isVisible ? '[&_img]:rotate-45' : ''}`
                )}
              >
                <ControlButton
                  onClick={toggleOverlay}
                  data={{
                    ariaLabel: 'Control button',
                    icon: {
                      altText: '',
                      asset: {
                        sys: {
                          uri: '/site-elements/icons/single-colour/ink-blue/plus.svg'
                        }
                      }
                    }
                  }}
                  colour={accentColour}
                  size={'large'}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

const FullScreenCarousel = ({ content }: FullCarouselProps) => {
  // const { slides } = content
  const slidesAreHydrated =
    Array.isArray(content.slides) &&
    content.slides.length > 0 &&
    content.slides.every(
      (slide: any) => slide.heading || slide.image || slide.video || slide.copy
    )

  const slideIds = Array.isArray(content.slides)
    ? content.slides.map((slide: any) => slide?.sys?.id).filter(Boolean)
    : []

  const { data, isLoading, error } = useQuery({
    queryKey: ['carousel-fullscreen-slides', slideIds],
    queryFn: async () => {
      return slideIds.length ? getEntriesByIds(slideIds) : []
    },
    enabled: !slidesAreHydrated && slideIds.length > 0
  })

  const dataById = useMemo(() => {
    return Array.isArray(data)
      ? data.reduce((acc: Record<string, any>, entry: any) => {
          acc[entry?.sys?.id] = entry
          return acc
        }, {})
      : {}
  }, [data])

  const hydratedSlides = useMemo(() => {
    return slidesAreHydrated
      ? content.slides
      : Array.isArray(content.slides)
        ? content.slides.map((slide: any) =>
            slide?.sys?.id ? dataById[slide.sys.id] || slide : slide
          )
        : []
  }, [slidesAreHydrated, content.slides, dataById])

  const sectionRef = useRef<HTMLElement | null>(null)
  const [progress, setProgress] = useState(0)

  const slideCount = Array.isArray(hydratedSlides) ? hydratedSlides.length : 0
  const threshold =
    slideCount > 0 ? Number(((100 / slideCount / 1.75) * 0.01).toFixed(2)) : 0.1

  // const threshold = hydratedSlides && (100 / hydratedSlides?.length / 1.75) * 0.01
  const { ref: intersectionRef, isIntersecting } = useIntersectionObserver({
    // threshold: Number(threshold?.toFixed(2))
    threshold
  })

  const setSectionRefs = (node: HTMLElement | null) => {
    sectionRef.current = node
    intersectionRef(node)
  }

  useEffect(() => {
    const handleScroll = () => {
      if (!sectionRef.current) return
      const rect = sectionRef.current.getBoundingClientRect()
      const sectionHeight = sectionRef.current.offsetHeight
      const windowHeight = window.innerHeight

      const scrollTop = Math.min(
        Math.max(0, windowHeight - rect.top),
        sectionHeight + windowHeight
      )

      const lastChild = sectionRef.current.lastChild as HTMLElement | null
      const lastChildHeight = lastChild ? lastChild.offsetHeight : 0
      const totalScrollableHeight =
        sectionHeight + windowHeight - lastChildHeight

      const progressValue = Math.min(
        100,
        Math.max(0, (scrollTop / totalScrollableHeight) * 100)
      )

      setProgress(progressValue)
    }

    window.addEventListener('scroll', handleScroll)
    handleScroll()
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  if (isLoading)
    return (
      <div className='py-l flex w-full justify-center'>
        <Loader fill={'#f5b6dc'} />
      </div>
    )
  if (error) return <div>Error loading component.</div>

  return (
    <section ref={setSectionRefs} className='relative h-full w-full'>
      <div
        className={cn(
          'h-s lg:right-xs -right-2xs fixed top-1/2 z-50 w-2xl -translate-y-1/2 rotate-90 transition-opacity duration-500',
          isIntersecting ? 'opacity-100' : 'opacity-0'
        )}
      >
        <Progress
          value={progress}
          barColour={'white'}
          className='h-2xl bg-grey-l/40 w-full'
        />
      </div>

      {Array.isArray(hydratedSlides) &&
        hydratedSlides.map((slide, index) => (
          <FullSlide slide={slide} key={`content-${index}`} />
        ))}
    </section>
  )
}

export default FullScreenCarousel
